import { createId } from "@/lib/ids";
import type { Rect } from "@/lib/math/rect";
import type { CurvaDocument, Page } from "./document";
import { findSceneNode, getActivePage } from "./document";
import type { FrameNode } from "./nodes";

export type GuideOrientation = "horizontal" | "vertical";

export interface Guide {
  id: string;
  orientation: GuideOrientation;
  /** Frame-local offset along the guide axis; page guides use document-space coordinates. */
  position: number;
}

export type GuideScope =
  | { kind: "page"; pageId: string }
  | { kind: "frame"; frameId: string };

export interface ResolvedGuide {
  id: string;
  orientation: GuideOrientation;
  position: number;
  scope: GuideScope;
  documentPosition: number;
  clipBounds?: Rect;
  frameSelected: boolean;
}

export interface GuideSelection {
  id: string;
  scope: GuideScope;
}

export function createGuide(orientation: GuideOrientation, position: number): Guide {
  return {
    id: createId("guide"),
    orientation,
    position,
  };
}

export function normalizeGuides(guides: Guide[] | undefined): Guide[] {
  if (!guides?.length) {
    return [];
  }

  return guides.map(guide => ({
    id: typeof guide.id === "string" ? guide.id : createId("guide"),
    orientation: guide.orientation === "vertical" ? "vertical" : "horizontal",
    position: Number.isFinite(guide.position) ? guide.position : 0,
  }));
}

export function pageGuides(page: Page): Guide[] {
  return normalizeGuides(page.guides);
}

export function frameGuides(frame: FrameNode): Guide[] {
  return normalizeGuides(frame.guides);
}

export function guideDocumentPosition(guide: Guide, frame: FrameNode | undefined): number {
  if (!frame) {
    return guide.position;
  }

  return guide.orientation === "vertical"
    ? frame.bounds.x + guide.position
    : frame.bounds.y + guide.position;
}

export function guideLocalPosition(guide: Guide, frame: FrameNode, documentPosition: number): number {
  return guide.orientation === "vertical"
    ? documentPosition - frame.bounds.x
    : documentPosition - frame.bounds.y;
}

export function collectResolvedGuides(
  document: CurvaDocument,
  activePageId: string | undefined,
  selectedNodeIds: string[],
): ResolvedGuide[] {
  const page = getActivePage(document, activePageId);
  if (!page) {
    return [];
  }

  const selected = new Set(selectedNodeIds);
  const resolved: ResolvedGuide[] = [];

  for (const guide of pageGuides(page)) {
    resolved.push({
      id: guide.id,
      orientation: guide.orientation,
      position: guide.position,
      scope: { kind: "page", pageId: page.id },
      documentPosition: guide.position,
      frameSelected: false,
    });
  }

  collectFrameGuides(page.children, selected, resolved);
  return resolved;
}

function collectFrameGuides(nodes: FrameNode["children"], selected: Set<string>, resolved: ResolvedGuide[]): void {
  for (const node of nodes) {
    if (node.type !== "frame") {
      continue;
    }

    const frameSelected = selected.has(node.id);
    for (const guide of frameGuides(node)) {
      resolved.push({
        id: guide.id,
        orientation: guide.orientation,
        position: guide.position,
        scope: { kind: "frame", frameId: node.id },
        documentPosition: guideDocumentPosition(guide, node),
        clipBounds: frameSelected ? undefined : node.bounds,
        frameSelected,
      });
    }

    collectFrameGuides(node.children, selected, resolved);
  }
}

export function findGuideSelection(
  document: CurvaDocument,
  activePageId: string | undefined,
  guideId: string,
): GuideSelection | undefined {
  const page = getActivePage(document, activePageId);
  if (!page) {
    return undefined;
  }

  if (pageGuides(page).some(guide => guide.id === guideId)) {
    return { id: guideId, scope: { kind: "page", pageId: page.id } };
  }

  return findGuideInNodes(page.children, guideId);
}

function findGuideInNodes(nodes: FrameNode["children"], guideId: string): GuideSelection | undefined {
  for (const node of nodes) {
    if (node.type !== "frame") {
      continue;
    }

    if (frameGuides(node).some(guide => guide.id === guideId)) {
      return { id: guideId, scope: { kind: "frame", frameId: node.id } };
    }

    const nested = findGuideInNodes(node.children, guideId);
    if (nested) {
      return nested;
    }
  }

  return undefined;
}

export function requireGuide(
  document: CurvaDocument,
  selection: GuideSelection,
): { guide: Guide; frame?: FrameNode } {
  if (selection.scope.kind === "page") {
    const pageId = selection.scope.pageId;
    const page = document.pages.find(candidate => candidate.id === pageId);
    if (!page) {
      throw new Error("Guide not found.");
    }

    page.guides = pageGuides(page);
    const guide = page.guides.find(candidate => candidate.id === selection.id);
    if (!guide) {
      throw new Error("Guide not found.");
    }
    return { guide };
  }

  const frame = findSceneNode(document, selection.scope.frameId);
  if (!frame || frame.type !== "frame") {
    throw new Error("Guide frame not found.");
  }

  frame.guides = frameGuides(frame);
  const guide = frame.guides.find(candidate => candidate.id === selection.id);
  if (!guide) {
    throw new Error("Guide not found.");
  }

  return { guide, frame };
}

export function guideScopesEqual(a: GuideScope, b: GuideScope): boolean {
  if (a.kind !== b.kind) {
    return false;
  }

  return a.kind === "page"
    ? b.kind === "page" && a.pageId === b.pageId
    : b.kind === "frame" && a.frameId === b.frameId;
}

export function guideSelectionsEqual(a: GuideSelection | undefined, b: GuideSelection | undefined): boolean {
  if (!a || !b) {
    return !a && !b;
  }

  return a.id === b.id && guideScopesEqual(a.scope, b.scope);
}
